import React, { useState, useEffect } from 'react';
import { Server, AlertCircle, CheckCircle, Clock, Settings, Power, PowerOff, Shield, Users, RefreshCw } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import './Dashboard.css';

function Dashboard({ guilds, maintenanceData, onUpdateMaintenance, user }) {
  const [selectedGuild, setSelectedGuild] = useState(null);
  const [message, setMessage] = useState('');
  const [estimatedEndTime, setEstimatedEndTime] = useState('');
  const [adminPermissions, setAdminPermissions] = useState({});
  const [roleSettings, setRoleSettings] = useState({});
  const [manageRoles, setManageRoles] = useState(true);
  const [isApplyingRoles, setIsApplyingRoles] = useState(false);
  const [isRestoringRoles, setIsRestoringRoles] = useState(false);

  useEffect(() => {
    // Check admin permissions for all guilds
    guilds.forEach(guild => {
      checkAdminPermission(guild.id);
    });
  }, [guilds]);

  const checkAdminPermission = async (guildId) => {
    try {
      const response = await fetch(`/api/auth/check-admin/${guildId}`);
      const data = await response.json();
      setAdminPermissions(prev => ({
        ...prev,
        [guildId]: data.hasPermission
      }));
    } catch (error) {
      console.error('Error checking admin permission:', error);
    }
  };

  const fetchRoleSettings = async (guildId) => {
    try {
      const response = await fetch(`/api/maintenance/${guildId}/role-settings`);
      const data = await response.json();
      setRoleSettings(prev => ({
        ...prev,
        [guildId]: data
      }));
      setManageRoles(data.manage_roles !== undefined ? data.manage_roles : true);
    } catch (error) {
      console.error('Error fetching role settings:', error);
    }
  };

  const handleToggleMaintenance = async (guild) => {
    const currentStatus = maintenanceData[guild.id];
    const newStatus = !currentStatus?.inMaintenance;
    
    await onUpdateMaintenance(guild.id, {
      inMaintenance: newStatus,
      maintenanceMessage: message || currentStatus?.maintenanceMessage || 'Serveur en maintenance',
      estimatedEndTime: estimatedEndTime || currentStatus?.estimatedEndTime || null,
      manageRoles: manageRoles
    });
    
    if (!newStatus) {
      setMessage('');
      setEstimatedEndTime('');
    }
  };

  const handleUpdateMessage = async (guild) => {
    await onUpdateMaintenance(guild.id, {
      inMaintenance: maintenanceData[guild.id]?.inMaintenance || false,
      maintenanceMessage: message,
      estimatedEndTime: estimatedEndTime
    });
  };

  const handleToggleManageRoles = async (guildId) => {
    try {
      const response = await fetch(`/api/maintenance/${guildId}/role-settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ manageRoles: !manageRoles })
      });
      const data = await response.json();
      setManageRoles(data.manage_roles);
      setRoleSettings(prev => ({
        ...prev,
        [guildId]: data
      }));
    } catch (error) {
      console.error('Error updating role settings:', error);
    }
  };

  const handleApplyRoles = async (guildId) => {
    setIsApplyingRoles(true);
    try {
      const response = await fetch(`/api/maintenance/${guildId}/apply-roles`, {
        method: 'POST'
      });
      const data = await response.json();
      if (data.success) {
        alert('Rôle maintenance appliqué à tous les membres');
      } else {
        alert(`Erreur: ${data.message}`);
      }
    } catch (error) {
      console.error('Error applying roles:', error);
      alert('Erreur lors de l\'application des rôles');
    } finally {
      setIsApplyingRoles(false);
    }
  };

  const handleRestoreRoles = async (guildId) => {
    setIsRestoringRoles(true);
    try {
      const response = await fetch(`/api/maintenance/${guildId}/restore-roles`, {
        method: 'POST'
      });
      const data = await response.json();
      if (data.success) {
        alert('Rôles originaux restaurés pour tous les membres');
      } else {
        alert(`Erreur: ${data.message}`);
      }
    } catch (error) {
      console.error('Error restoring roles:', error);
      alert('Erreur lors de la restauration des rôles');
    } finally {
      setIsRestoringRoles(false);
    }
  };

  const handleSelectGuild = async (guild) => {
    setSelectedGuild(guild);
    await fetchRoleSettings(guild.id);
    const currentStatus = maintenanceData[guild.id];
    if (currentStatus) {
      setMessage(currentStatus.maintenanceMessage || '');
      setEstimatedEndTime(currentStatus.estimatedEndTime || '');
    }
  };

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h2>
          <Server size={24} />
          Gestion des Serveurs Discord
        </h2>
        <p className="subtitle">Gérez la maintenance de vos serveurs en temps réel</p>
      </div>

      <div className="dashboard-content">
        <div className="guilds-list">
          {guilds.length === 0 ? (
            <div className="empty-state">
              <AlertCircle size={48} />
              <p>Aucun serveur Discord connecté</p>
              <p className="small">Assurez-vous que votre bot est invité sur vos serveurs</p>
            </div>
          ) : (
            guilds.map((guild) => {
              const status = maintenanceData[guild.id];
              const isInMaintenance = status?.inMaintenance;
              
              const hasPermission = adminPermissions[guild.id];
              
              return (
                <div
                  key={guild.id}
                  className={`guild-card ${isInMaintenance ? 'in-maintenance' : ''} ${!hasPermission ? 'no-permission' : ''}`}
                  onClick={() => hasPermission && handleSelectGuild(guild)}
                >
                  <div className="guild-info">
                    <div className="guild-icon">
                      {guild.icon ? (
                        <img src={guild.icon} alt={guild.name} />
                      ) : (
                        <Server size={32} />
                      )}
                    </div>
                    <div className="guild-details">
                      <h3>{guild.name}</h3>
                      <p className="guild-id">ID: {guild.id}</p>
                      <p className="member-count">{guild.memberCount} membres</p>
                    </div>
                  </div>
                  <div className="guild-status">
                    {hasPermission ? (
                      <span className="status-badge admin">
                        <Shield size={16} />
                        Admin
                      </span>
                    ) : (
                      <span className="status-badge no-admin">
                        <AlertCircle size={16} />
                        Pas admin
                      </span>
                    )}
                    {isInMaintenance && (
                      <span className="status-badge maintenance">
                        <AlertCircle size={16} />
                        En maintenance
                      </span>
                    )}
                    {!isInMaintenance && hasPermission && (
                      <span className="status-badge online">
                        <CheckCircle size={16} />
                        En ligne
                      </span>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {selectedGuild && (
          <div className="maintenance-panel">
            <div className="panel-header">
              <div className="selected-guild-info">
                <div className="guild-icon">
                  {selectedGuild.icon ? (
                    <img src={selectedGuild.icon} alt={selectedGuild.name} />
                  ) : (
                    <Server size={32} />
                  )}
                </div>
                <div>
                  <h3>{selectedGuild.name}</h3>
                  <p className="guild-id">ID: {selectedGuild.id}</p>
                </div>
              </div>
              <button
                className="close-btn"
                onClick={() => setSelectedGuild(null)}
              >
                ×
              </button>
            </div>

            <div className="panel-content">
              <div className="status-section">
                <h4>
                  <Settings size={20} />
                  Statut de maintenance
                </h4>
                <button
                  className={`toggle-btn ${maintenanceData[selectedGuild.id]?.inMaintenance ? 'active' : ''}`}
                  onClick={() => handleToggleMaintenance(selectedGuild)}
                >
                  {maintenanceData[selectedGuild.id]?.inMaintenance ? (
                    <>
                      <PowerOff size={20} />
                      Désactiver la maintenance
                    </>
                  ) : (
                    <>
                      <Power size={20} />
                      Activer la maintenance
                    </>
                  )}
                </button>
              </div>

              <div className="message-section">
                <h4>Message de maintenance (Markdown supporté)</h4>
                <textarea
                  value={message || maintenanceData[selectedGuild.id]?.maintenanceMessage || ''}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Entrez le message de maintenance... (Markdown supporté: **gras**, *italique*, `code`, etc.)"
                  rows={4}
                />
                <div className="markdown-preview">
                  <h5>Prévisualisation:</h5>
                  <div className="preview-content">
                    <ReactMarkdown>
                      {message || maintenanceData[selectedGuild.id]?.maintenanceMessage || 'Aucun message'}
                    </ReactMarkdown>
                  </div>
                </div>
                <button
                  className="update-btn"
                  onClick={() => handleUpdateMessage(selectedGuild)}
                >
                  Mettre à jour le message
                </button>
              </div>

              <div className="time-section">
                <h4>
                  <Clock size={20} />
                  Heure de fin estimée
                </h4>
                <input
                  type="datetime-local"
                  value={estimatedEndTime || maintenanceData[selectedGuild.id]?.estimatedEndTime?.slice(0, 16) || ''}
                  onChange={(e) => setEstimatedEndTime(e.target.value)}
                />
              </div>

              <div className="role-management-section">
                <h4>
                  <Users size={20} />
                  Gestion des rôles Discord
                </h4>
                <div className="role-toggle">
                  <label className="toggle-label">
                    <input
                      type="checkbox"
                      checked={manageRoles}
                      onChange={() => handleToggleManageRoles(selectedGuild.id)}
                    />
                    <span>Gérer automatiquement les rôles pendant la maintenance</span>
                  </label>
                </div>
                <div className="role-actions">
                  <button
                    className="role-btn apply-btn"
                    onClick={() => handleApplyRoles(selectedGuild.id)}
                    disabled={isApplyingRoles}
                  >
                    {isApplyingRoles ? (
                      <>
                        <RefreshCw size={16} className="spin" />
                        Application en cours...
                      </>
                    ) : (
                      <>
                        <Users size={16} />
                        Appliquer le rôle maintenance
                      </>
                    )}
                  </button>
                  <button
                    className="role-btn restore-btn"
                    onClick={() => handleRestoreRoles(selectedGuild.id)}
                    disabled={isRestoringRoles}
                  >
                    {isRestoringRoles ? (
                      <>
                        <RefreshCw size={16} className="spin" />
                        Restauration en cours...
                      </>
                    ) : (
                      <>
                        <RefreshCw size={16} />
                        Restaurer les rôles originaux
                      </>
                    )}
                  </button>
                </div>
                <p className="role-info">
                  ⚠️ Le bot créera automatiquement un rôle "Maintenance" (rouge) et l'appliquera à tous les membres (sauf admins et bots) pendant la maintenance.
                  Les rôles originaux seront sauvegardés et restaurés automatiquement.
                </p>
              </div>

              {maintenanceData[selectedGuild.id]?.inMaintenance && (
                <div className="maintenance-info">
                  <h4>Informations de maintenance</h4>
                  <p>
                    <strong>Début:</strong> {new Date(maintenanceData[selectedGuild.id].startTime).toLocaleString('fr-FR')}
                  </p>
                  {maintenanceData[selectedGuild.id].estimatedEndTime && (
                    <p>
                      <strong>Fin estimée:</strong> {new Date(maintenanceData[selectedGuild.id].estimatedEndTime).toLocaleString('fr-FR')}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Dashboard;
